import { container } from './container';
import { TYPES } from './tokens';
import { httpClient } from '@/infrastructure/http/client/httpClient';
import { ApiAuthRepository } from '@/infrastructure/repositories/ApiAuthRepository';
import { ApiTenantRepository } from '@/infrastructure/repositories/ApiTenantRepository';
import { ApiPlanRepository } from '@/infrastructure/repositories/ApiPlanRepository';
import { ApiAdicionalRepository } from '@/infrastructure/repositories/ApiAdicionalRepository';
import { ApiTenantAdicionalRepository } from '@/infrastructure/repositories/ApiTenantAdicionalRepository';
import { ApiPanelPagoRepository } from '@/infrastructure/repositories/ApiPanelPagoRepository';
import { ApiPanelFacturacionRepository } from '@/infrastructure/repositories/ApiPanelFacturacionRepository';
import { ApiReportRepository } from '@/infrastructure/repositories/ApiReportRepository';
import { MockAuthRepository } from '@/infrastructure/repositories/MockAuthRepository';
import { MockTenantRepository } from '@/infrastructure/repositories/MockTenantRepository';
import { MockPlanRepository } from '@/infrastructure/repositories/MockPlanRepository';
import { MockPanelFacturacionRepository } from '@/infrastructure/repositories/MockPanelFacturacionRepository';
import { MockPanelPagoRepository } from '@/infrastructure/repositories/MockPanelPagoRepository';
import { MercadoPagoRepository } from '@/infrastructure/repositories/MercadoPagoRepository';
import { ApiSupportTicketRepository } from '@/infrastructure/repositories/ApiSupportTicketRepository';
import { ApiEmailOutboundRepository } from '@/infrastructure/repositories/ApiEmailOutboundRepository';
import { ApiHelpDocumentationRepository } from '@/infrastructure/repositories/ApiHelpDocumentationRepository';
import type {
  IAuthRepository,
  ITenantRepository,
  IPlanRepository,
  IPanelFacturacionRepository,
  IPanelPagoRepository,
  IMercadoPagoRepository,
  IAdicionalRepository,
  IReportRepository,
} from '@/domain/repositories';
import type { ITenantAdicionalRepository } from '@/domain/repositories/ITenantAdicionalRepository';
import type { ISupportTicketRepository } from '@/domain/repositories/ISupportTicketRepository';
import type { IEmailOutboundRepository } from '@/domain/repositories/IEmailOutboundRepository';
import type { IHelpDocumentationRepository } from '@/domain/repositories/IHelpDocumentationRepository';
import {
  LoginAction,
  Enable2FAAction,
  Verify2FASetupAction,
  Disable2FAAction,
  GetCurrentUserAction,
  LogoutAction,
  ChangeUserTenantAction,
  ListTenantsAction,
  GetTenantAction,
  CreateTenantAction,
  UpdateTenantAction,
  DeleteTenantAction,
  ChangeTenantStatusAction,
  ListPlansAction,
  CreatePlanAction,
  UpdatePlanAction,
  TogglePlanStatusAction,
  DeletePlanAction,
  ListAdicionalesAction,
  CreateAdicionalAction,
  UpdateAdicionalAction,
  ToggleAdicionalStatusAction,
  DeleteAdicionalAction,
  ListBillingPanelsAction,
  GetBillingPanelAction,
  ListPanelPagosAction,
  CancelPanelPagoAction,
  CreateMpPreferenceAction,
  GetFinancialReportAction,
  GetClinicReportAction,
  GetCollectionsReportAction,
  GetSaasMetricsAction,
  GetEcosystemUsageAction,
  GetReminderAnalyticsAction,
} from '@/application/actions';
import { ListTicketsAction } from '@/application/actions/support/ListTicketsAction';
import { UpdateTicketAction } from '@/application/actions/support/UpdateTicketAction';
import { ListEmailsAction } from '@/application/actions/emails/ListEmailsAction';
import {
  GetHelpArticleAction,
  GetHelpArticleBundleAction,
  GetHelpLayoutAction,
  ListHelpArticlesAction,
  ListHelpCategoriesAction,
  SaveHelpArticleAction,
  SaveHelpArticleBundleAction,
  SaveHelpCategoryAction,
  SaveHelpLayoutAction,
  TranslateHelpTextAction,
} from '@/application/actions/help/HelpDocumentationActions';

// Cambiar a false para usar los repositorios reales (API)
// Ahora usamos los repositorios API por defecto (conectados al backend)
const USE_MOCK_REPOSITORY = false;

export function configureDependencies(): void {
  // Bind HTTP Client
  container.bind(TYPES.HttpClient).toConstantValue(httpClient);

  // Bind Repositories (Port → Adapter)
  if (USE_MOCK_REPOSITORY) {
    container.bind<IAuthRepository>(TYPES.IAuthRepository).to(MockAuthRepository);
    container.bind<ITenantRepository>(TYPES.ITenantRepository).to(MockTenantRepository);
    container.bind<IPlanRepository>(TYPES.IPlanRepository).to(MockPlanRepository);
    container.bind<IAdicionalRepository>(TYPES.IAdicionalRepository).to(ApiAdicionalRepository);
    container.bind<ITenantAdicionalRepository>(TYPES.ITenantAdicionalRepository).to(ApiTenantAdicionalRepository);
    container.bind<IPanelFacturacionRepository>(TYPES.IPanelFacturacionRepository).to(MockPanelFacturacionRepository);
    container.bind<IPanelPagoRepository>(TYPES.IPanelPagoRepository).to(MockPanelPagoRepository);
  } else {
    // Usar repositorios API (conectados al backend)
    container.bind<IAuthRepository>(TYPES.IAuthRepository).to(ApiAuthRepository);
    container.bind<ITenantRepository>(TYPES.ITenantRepository).to(ApiTenantRepository);
    container.bind<IPlanRepository>(TYPES.IPlanRepository).to(ApiPlanRepository);
    container.bind<IAdicionalRepository>(TYPES.IAdicionalRepository).to(ApiAdicionalRepository);
    container.bind<ITenantAdicionalRepository>(TYPES.ITenantAdicionalRepository).to(ApiTenantAdicionalRepository);
    container.bind<IPanelPagoRepository>(TYPES.IPanelPagoRepository).to(ApiPanelPagoRepository);
    container.bind<IPanelFacturacionRepository>(TYPES.IPanelFacturacionRepository).to(ApiPanelFacturacionRepository);
  }
  
  // Mercado Pago siempre usa el repositorio real (API)
  container.bind<IMercadoPagoRepository>(TYPES.IMercadoPagoRepository).to(MercadoPagoRepository).inSingletonScope();
  container.bind<IReportRepository>(TYPES.IReportRepository).to(ApiReportRepository).inSingletonScope();

  // Bind Actions - Auth
  container.bind<LoginAction>(TYPES.LoginAction).to(LoginAction).inSingletonScope();
  container.bind<Enable2FAAction>(TYPES.Enable2FAAction).to(Enable2FAAction).inSingletonScope();
  container.bind<Verify2FASetupAction>(TYPES.Verify2FASetupAction).to(Verify2FASetupAction).inSingletonScope();
  container.bind<Disable2FAAction>(TYPES.Disable2FAAction).to(Disable2FAAction).inSingletonScope();
  container.bind<GetCurrentUserAction>(TYPES.GetCurrentUserAction).to(GetCurrentUserAction).inSingletonScope();
  container.bind<LogoutAction>(TYPES.LogoutAction).to(LogoutAction).inSingletonScope();
  container.bind<ChangeUserTenantAction>(TYPES.ChangeUserTenantAction).to(ChangeUserTenantAction).inSingletonScope();

  // Bind Actions - Tenant
  container.bind<ListTenantsAction>(TYPES.ListTenantsAction).to(ListTenantsAction).inSingletonScope();
  container.bind<GetTenantAction>(TYPES.GetTenantAction).to(GetTenantAction).inSingletonScope();
  container.bind<CreateTenantAction>(TYPES.CreateTenantAction).to(CreateTenantAction).inSingletonScope();
  container.bind<UpdateTenantAction>(TYPES.UpdateTenantAction).to(UpdateTenantAction).inSingletonScope();
  container.bind<DeleteTenantAction>(TYPES.DeleteTenantAction).to(DeleteTenantAction).inSingletonScope();
  container.bind<ChangeTenantStatusAction>(TYPES.ChangeTenantStatusAction).to(ChangeTenantStatusAction).inSingletonScope();

  // Bind Actions - Plan
  container.bind<ListPlansAction>(TYPES.ListPlansAction).to(ListPlansAction).inSingletonScope();
  container.bind<CreatePlanAction>(TYPES.CreatePlanAction).to(CreatePlanAction).inSingletonScope();
  container.bind<UpdatePlanAction>(TYPES.UpdatePlanAction).to(UpdatePlanAction).inSingletonScope();
  container.bind<TogglePlanStatusAction>(TYPES.TogglePlanStatusAction).to(TogglePlanStatusAction).inSingletonScope();
  container.bind<DeletePlanAction>(TYPES.DeletePlanAction).to(DeletePlanAction).inSingletonScope();

  // Bind Actions - Adicional
  container.bind<ListAdicionalesAction>(TYPES.ListAdicionalesAction).to(ListAdicionalesAction).inSingletonScope();
  container.bind<CreateAdicionalAction>(TYPES.CreateAdicionalAction).to(CreateAdicionalAction).inSingletonScope();
  container.bind<UpdateAdicionalAction>(TYPES.UpdateAdicionalAction).to(UpdateAdicionalAction).inSingletonScope();
  container.bind<ToggleAdicionalStatusAction>(TYPES.ToggleAdicionalStatusAction).to(ToggleAdicionalStatusAction).inSingletonScope();
  container.bind<DeleteAdicionalAction>(TYPES.DeleteAdicionalAction).to(DeleteAdicionalAction).inSingletonScope();

  // Bind Actions - Billing
  container.bind<ListBillingPanelsAction>(TYPES.ListBillingPanelsAction).to(ListBillingPanelsAction).inSingletonScope();
  container.bind<GetBillingPanelAction>(TYPES.GetBillingPanelAction).to(GetBillingPanelAction).inSingletonScope();

  // Bind Actions - Pagos
  container.bind<ListPanelPagosAction>(TYPES.ListPanelPagosAction).to(ListPanelPagosAction).inSingletonScope();
  container.bind<CancelPanelPagoAction>(TYPES.CancelPanelPagoAction).to(CancelPanelPagoAction).inSingletonScope();
  
  // Bind Actions - Mercado Pago
  container.bind<CreateMpPreferenceAction>(TYPES.CreateMpPreferenceAction).to(CreateMpPreferenceAction).inSingletonScope();

  // Bind Actions - Reports
  container.bind<GetFinancialReportAction>(TYPES.GetFinancialReportAction).to(GetFinancialReportAction).inSingletonScope();
  container.bind<GetClinicReportAction>(TYPES.GetClinicReportAction).to(GetClinicReportAction).inSingletonScope();
  container.bind<GetCollectionsReportAction>(TYPES.GetCollectionsReportAction).to(GetCollectionsReportAction).inSingletonScope();
  container.bind<GetSaasMetricsAction>(TYPES.GetSaasMetricsAction).to(GetSaasMetricsAction).inSingletonScope();
  container.bind<GetEcosystemUsageAction>(TYPES.GetEcosystemUsageAction).to(GetEcosystemUsageAction).inSingletonScope();
  container.bind<GetReminderAnalyticsAction>(TYPES.GetReminderAnalyticsAction).to(GetReminderAnalyticsAction).inSingletonScope();

  // Bind Repositories - Support
  container.bind<ISupportTicketRepository>(TYPES.ISupportTicketRepository).to(ApiSupportTicketRepository).inSingletonScope();

  // Bind Actions - Support
  container.bind<ListTicketsAction>(TYPES.ListTicketsAction).to(ListTicketsAction).inSingletonScope();
  container.bind<UpdateTicketAction>(TYPES.UpdateTicketAction).to(UpdateTicketAction).inSingletonScope();

  // Bind Repositories - Emails (SendGrid)
  container.bind<IEmailOutboundRepository>(TYPES.IEmailOutboundRepository).to(ApiEmailOutboundRepository).inSingletonScope();

  // Bind Actions - Emails
  container.bind<ListEmailsAction>(TYPES.ListEmailsAction).to(ListEmailsAction).inSingletonScope();

  // Bind Repositories - Help documentation
  container.bind<IHelpDocumentationRepository>(TYPES.IHelpDocumentationRepository).to(ApiHelpDocumentationRepository).inSingletonScope();

  // Bind Actions - Help documentation
  container.bind<ListHelpCategoriesAction>(TYPES.ListHelpCategoriesAction).to(ListHelpCategoriesAction).inSingletonScope();
  container.bind<SaveHelpCategoryAction>(TYPES.SaveHelpCategoryAction).to(SaveHelpCategoryAction).inSingletonScope();
  container.bind<ListHelpArticlesAction>(TYPES.ListHelpArticlesAction).to(ListHelpArticlesAction).inSingletonScope();
  container.bind<GetHelpArticleAction>(TYPES.GetHelpArticleAction).to(GetHelpArticleAction).inSingletonScope();
  container.bind<GetHelpArticleBundleAction>(TYPES.GetHelpArticleBundleAction).to(GetHelpArticleBundleAction).inSingletonScope();
  container.bind<SaveHelpArticleBundleAction>(TYPES.SaveHelpArticleBundleAction).to(SaveHelpArticleBundleAction).inSingletonScope();
  container.bind<TranslateHelpTextAction>(TYPES.TranslateHelpTextAction).to(TranslateHelpTextAction).inSingletonScope();
  container.bind<SaveHelpArticleAction>(TYPES.SaveHelpArticleAction).to(SaveHelpArticleAction).inSingletonScope();
  container.bind<GetHelpLayoutAction>(TYPES.GetHelpLayoutAction).to(GetHelpLayoutAction).inSingletonScope();
  container.bind<SaveHelpLayoutAction>(TYPES.SaveHelpLayoutAction).to(SaveHelpLayoutAction).inSingletonScope();
}

export function getContainer() {
  return container;
}
